import React from "react";

// Shared vocabulary for the PAEI Analysis screen.
//
// Two distinctions in here carry the whole model and must never be collapsed:
//
//   * short vs. over. A holder *below* what a slot needs is a control that will
//     not happen; a holder far *above* it is money being paid for capability the
//     work never uses. Rendering both as "wrong" destroys the asymmetry the
//     matcher is built on, so they get danger and warning respectively.
//   * the four role hues. They are mutually distinguishable and used
//     consistently everywhere (bars, chips, deltas) so a reader learns them once.

export const ROLE_KEYS = ["p", "a", "e", "i"];

export const ROLE_META = {
  p: { letter: "P", label: "Producer" },
  a: { letter: "A", label: "Administrator" },
  e: { letter: "E", label: "Entrepreneur" },
  i: { letter: "I", label: "Integrator" },
};

// Surplus of this many points or more is capability being paid for and not used.
export const OVER_QUALIFIED_THRESHOLD = 15;

/* --------------------------------- format -------------------------------- */

export const isNum = (v) => v != null && v !== "" && Number.isFinite(Number(v));

export const fmtMoney = (v) => (isNum(v) ? Number(v).toFixed(2) : "—");
export const fmtLevel = (v) => (isNum(v) ? String(Math.round(Number(v))) : "—");
export const fmtRate = (v) => (isNum(v) ? `${Number(v).toFixed(2)}/h` : "—");

export const fmtMinutes = (v) => {
  if (!isNum(v)) return "—";
  const m = Number(v);
  if (m >= 120) return `${(m / 60).toFixed(1)} h`;
  return `${m.toFixed(1)} m`;
};

export const fmtSignedMoney = (v) => {
  if (!isNum(v)) return "—";
  const n = Number(v);
  if (n === 0) return "0.00";
  // A saving reads as a minus, an increase as a plus. A negative `saving` means
  // the reassignment costs MORE — it must not be rendered as a smaller saving.
  return `${n > 0 ? "−" : "+"}${Math.abs(n).toFixed(2)}`;
};

/* --------------------------------- deltas -------------------------------- */

// Percentage change against the AS-IS column. `invert` marks the one metric
// where higher is better (PAEI fit) so its arrows point the right way.
export function computeDelta(value, baseline, invert = false) {
  if (!isNum(value) || !isNum(baseline) || Number(baseline) === 0) return null;
  const pct = ((Number(value) - Number(baseline)) / Math.abs(Number(baseline))) * 100;
  if (Math.abs(pct) < 0.05) return null; // noise, not a result
  return { pct, improved: invert ? pct > 0 : pct < 0 };
}

export function Delta({ value, baseline, invert = false }) {
  const d = computeDelta(value, baseline, invert);
  if (!d) return null;
  return (
    <span className={`pa-delta ${d.improved ? "up" : "down"}`}>
      {d.pct > 0 ? "+" : "−"}
      {Math.abs(d.pct).toFixed(1)}%
    </span>
  );
}

/* ---------------------------------- chips -------------------------------- */

export function scoreState(score, required) {
  if (!isNum(score) || !isNum(required)) return "ok";
  const gap = Number(score) - Number(required);
  if (gap < 0) return "short";
  if (gap >= OVER_QUALIFIED_THRESHOLD) return "over";
  return "ok";
}

/** `75 (+3)` against what the slot needs, coloured by which failure it is. */
export function ScoreChip({ score, required, role }) {
  if (!isNum(score)) return <span className="pa-score muted">—</span>;
  // With no requirement to judge against there is no verdict to render. Showing
  // the level in "ok" green would be a claim the payload never made.
  if (!isNum(required)) {
    return (
      <span className="pa-score muted">
        {role ? <i className={`pa-role-dot role-${role}`} aria-hidden="true" /> : null}
        {fmtLevel(score)}
      </span>
    );
  }
  const state = scoreState(score, required);
  const gap = isNum(required) ? Math.round(Number(score) - Number(required)) : null;
  const title =
    state === "short" ? "Below what this slot needs — the control may not happen"
    : state === "over" ? "Capability being paid for that this step does not use"
    : "Covered";
  return (
    <span className={`pa-score ${state}`} title={title}>
      {role ? <i className={`pa-role-dot role-${role}`} aria-hidden="true" /> : null}
      {fmtLevel(score)}
      {gap != null && gap !== 0 ? (
        <em>({gap > 0 ? "+" : "−"}{Math.abs(gap)})</em>
      ) : null}
    </span>
  );
}

/** The four PAEI levels of a job or a requirement, as compact letter chips. */
export function PaeiChipRow({ values, required = null }) {
  if (!values) return <span className="pa-muted">—</span>;
  return (
    <span className="pa-chip-row">
      {ROLE_KEYS.map((k) => {
        const state = required ? scoreState(values[k], required[k]) : null;
        return (
          <span
            key={k}
            className={`pa-chip role-${k}${state ? ` state-${state}` : ""}`}
            title={ROLE_META[k].label}
          >
            <b>{ROLE_META[k].letter}</b> {fmtLevel(values[k])}
          </span>
        );
      })}
    </span>
  );
}

/* ---------------------------------- bars --------------------------------- */

/**
 * Four-row PAEI bar block, in two variants:
 *   - single value (demand profile, required levels)
 *   - demand vs. supply — the requirement drawn as a dashed outline over the
 *     filled supply bar, numbered `supply/required`. This is the clearest single
 *     view of a gap, so it is worth the extra markup.
 */
export function PaeiBars({ values, required = null, compact = false }) {
  if (!values && !required) return null;
  const supply = values || {};
  return (
    <div className={`pa-bars${compact ? " compact" : ""}`}>
      {ROLE_KEYS.map((k) => {
        const sv = isNum(supply[k]) ? Number(supply[k]) : null;
        const rv = required && isNum(required[k]) ? Number(required[k]) : null;
        const state = rv != null && sv != null ? scoreState(sv, rv) : null;
        return (
          <div className="pa-bar-row" key={k}>
            <span className={`pa-bar-label role-${k}`} title={ROLE_META[k].label}>
              {ROLE_META[k].letter}
            </span>
            <span className="pa-bar-track">
              <i
                className={`pa-bar-fill role-${k}`}
                style={{ width: `${Math.max(0, Math.min(100, sv ?? rv ?? 0))}%` }}
              />
              {rv != null && values ? (
                <i
                  className="pa-bar-required"
                  style={{ left: `${Math.max(0, Math.min(100, rv))}%` }}
                  title={`needs ${fmtLevel(rv)}`}
                />
              ) : null}
            </span>
            <span className={`pa-bar-num${state ? ` state-${state}` : ""}`}>
              {rv != null && values ? `${fmtLevel(sv)}/${fmtLevel(rv)}` : fmtLevel(sv ?? rv)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------- status bar ----------------------------- */

/** Full-width bar above the tabs: a state-coloured left border, a short tag,
 *  the server's note rendered verbatim, and right-aligned muted metadata. */
export function StatusBar({ tone = "ok", tag, children, meta = null }) {
  return (
    <div className={`pa-statusbar tone-${tone}`}>
      <span className="pa-statusbar-tag">{tag}</span>
      <div className="pa-statusbar-body">{children}</div>
      {meta && meta.length > 0 ? (
        <div className="pa-statusbar-meta">
          {meta.filter(Boolean).map((m, i) => (
            <span key={i}>{m}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------ heuristic block -------------------------- */

/** One demand-shift heuristic. Shared by the Redesign tab (applied in this run)
 *  and the Shift reference tab (all nine, tagged applied / not applied). */
export function HeuristicBlock({ item, applied }) {
  const delta = item?.delta || null;
  const relocates = item?.relocates || null;
  const targets = item?.target_task_names || [];

  return (
    <div className={`pa-heuristic${applied ? " applied" : ""}`}>
      <div className="pa-heuristic-head">
        <h4>{item.heuristic}</h4>
        <span className={`pa-tag ${applied ? "applied" : "idle"}`}>
          {applied ? "applied in this run" : "not applied"}
        </span>
        <span className="pa-heuristic-deltas">
          {delta &&
            ROLE_KEYS.filter((k) => isNum(delta[k])).map((k) => (
              <span key={k} className={`pa-chip role-${k}`} title={`${ROLE_META[k].label} demand`}>
                <b>{ROLE_META[k].letter}</b>{" "}
                {Number(delta[k]) > 0 ? "+" : "−"}
                {Math.abs(Math.round(Number(delta[k])))}
              </span>
            ))}
        </span>
      </div>

      {targets.length > 0 && (
        <div className="pa-heuristic-on">
          on: <span>{targets.join(" · ")}</span>
        </div>
      )}

      {item.headline && <p className="pa-heuristic-headline">{item.headline}</p>}
      {item.detail && <p className="pa-heuristic-detail">{item.detail}</p>}

      {relocates && (
        <div className="pa-relocate-callout">
          <strong>{relocates.role_label || ROLE_META[relocates.role]?.label} demand does not
          disappear</strong> — it moves from the <b>{relocates.from_slot}</b> slot onto the{" "}
          <b>{relocates.to_slot}</b> slot on the same step. Check that whoever holds{" "}
          {relocates.to_slot} was assessed for it.
        </div>
      )}
    </div>
  );
}
